/**
 * Account-level Clients API: CRM-style client records that belong to the
 * workspace (the account), one level above any single project. Every member of
 * the workspace sees the same clients; viewers read, planners and above write.
 *
 * Deliberately does NOT go through PlanningApiClient's `planningPath()`: that
 * helper puts the active project in the URL, the wrong scope for a record that
 * belongs to the account. It uses the shared account-level client instead, so
 * errors come back as plain-language ApiErrors and an expired login ends the
 * session the same way as everywhere else.
 */
import { accountRequest } from './apiClient';
import { withOldRouteFallback } from '../utils/routeFallback';

export interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
  /** Projects linked to this client. */
  project_count: number;
}

export interface ClientProject {
  id: string;
  name: string;
  project_date: string | null;
}

export interface ClientWithProjects extends Client {
  projects: ClientProject[];
}

export type ClientArchiveFilter = 'active' | 'archived' | 'all';

export interface ClientInput {
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  /** Links the new client to one of the workspace's projects. */
  project_id?: string;
}

const request = accountRequest;

/** List clients for the account, optionally filtered by search text and archive status. */
export async function loadClients(search: string, archived: ClientArchiveFilter): Promise<Client[]> {
  try {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    params.set('archived', archived);
    const res = await request<{ success: true; data: Client[] }>(`/planning/account/clients?${params.toString()}`);
    return res.data;
  } catch (error) {
    console.error('Failed to load clients:', error);
    throw error instanceof Error ? error : new Error('Failed to load clients.');
  }
}

export async function getClientById(id: string): Promise<ClientWithProjects> {
  const res = await request<{ success: true; data: ClientWithProjects }>(`/planning/account/clients/${id}`);
  return res.data;
}

export async function createClient(input: ClientInput): Promise<Client> {
  const res = await request<{ success: true; data: Client }>('/planning/account/clients', {
    method: 'POST',
    body: input,
  });
  return res.data;
}

export async function updateClient(
  id: string,
  updates: Partial<Pick<ClientInput, 'name' | 'email' | 'phone' | 'notes'>>
): Promise<Client> {
  const res = await request<{ success: true; data: Client }>(`/planning/account/clients/${id}`, {
    method: 'PUT',
    body: updates,
  });
  return res.data;
}

/**
 * Links the client to a project. Calls `link-project`; an older server that
 * only has the `link-wedding` route (404 or 405) gets that one instead.
 */
export async function linkClientToProject(id: string, projectId: string): Promise<Client> {
  const link = (route: 'link-project' | 'link-wedding') => async () => {
    const res = await request<{ success: true; data: Client }>(`/planning/account/clients/${id}/${route}`, {
      method: 'POST',
      body: { project_id: projectId },
    });
    return res.data;
  };
  return withOldRouteFallback(link('link-project'), link('link-wedding'));
}

/** Archives the client (default) or permanently deletes it (only valid once already archived). */
export async function deleteClient(id: string, permanent = false): Promise<void> {
  await request<{ success: true }>(`/planning/account/clients/${id}${permanent ? '?permanent=true' : ''}`, {
    method: 'DELETE',
  });
}

/** Un-archives a previously archived client. */
export async function restoreClient(id: string): Promise<Client> {
  const res = await request<{ success: true; data: Client }>(`/planning/account/clients/${id}/restore`, {
    method: 'POST',
  });
  return res.data;
}
